import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";

const AT_CATEGORIES = [
  {
    category: "Kitchen Aids",
    icon: "🍴",
    description: "Adaptations to reduce grip force, wrist load, and fatigue during food preparation and eating.",
    products: [
      { name: "Kettle tipper / lever", indication: "Reduced wrist extension, grip weakness", notes: "Assess mains vs cordless; check weight of filled kettle" },
      { name: "Electric jar opener", indication: "Ulnar drift, Z-thumb, grip weakness", notes: "Many models available — assess for ease of one-handed operation" },
      { name: "Non-slip mats (Dycem)", indication: "Reduced grip — used under bowls, boards, jars", notes: "Washable, multiple colours. Also useful under equipment during stabilisation" },
      { name: "Built-up handled cutlery (e.g. Caring Cutlery)", indication: "Reduced MCP grip, pain", notes: "Various handle diameters available. Assess palmar grip vs cylindrical grip" },
      { name: "Perch stool / kitchen stool", indication: "Fatigue, reduced standing tolerance", notes: "Check kitchen layout for suitable surfaces — not appropriate for all kitchens" },
      { name: "Rocker knife / angled knife", indication: "Reduced wrist extension, unilateral weakness", notes: "Rocker knife requires less force than conventional cutting" },
    ],
  },
  {
    category: "Dressing Aids",
    icon: "👔",
    description: "Equipment to support independent dressing where joint range, grip, or dexterity is reduced.",
    products: [
      { name: "Dressing stick (with hook)", indication: "Reduced shoulder range, trunk bending", notes: "Multiple uses: pushing clothing off shoulders, pulling up socks" },
      { name: "Long-handled shoe horn", indication: "Reduced trunk flexion, hip flexion (axSpA, hip OA)", notes: "Various lengths available — 600mm suitable for most" },
      { name: "Button hook", indication: "Reduced finger dexterity, SSc hand", notes: "Standard and 'self-feeding' types. Check loop width vs button size" },
      { name: "Elastic laces / Velcro fastenings", indication: "Reduced dexterity, fatigue", notes: "Promotes independence; assess shoe opening width" },
      { name: "Sock aid", indication: "Reduced hip flexion, trunk bending, grip", notes: "Assess whether patient can thread sock — consider carer involvement if not" },
      { name: "Zip pulls / zip extenders", indication: "Reduced dexterity, grip", notes: "Can be retrofitted to most clothing" },
    ],
  },
  {
    category: "Writing Aids",
    icon: "✏️",
    description: "Aids to reduce effort during writing, typing, and office tasks.",
    products: [
      { name: "Wide grip pen (Yoropen, Skey, Penagain)", indication: "Reduced grip, MCP pain, SSc", notes: "Yoropen angled design reduces ulnar-deviating grip force" },
      { name: "Pen holder splint (custom)", indication: "Inability to maintain pen in hand due to grip weakness", notes: "Custom thermoplastic by OT; functional positioning essential" },
      { name: "Ergonomic keyboard (split or contoured)", indication: "Wrist pain during typing, carpal tunnel", notes: "Microsoft Sculpt, Logitech K860 — assess typing posture comprehensively" },
      { name: "Vertical mouse / trackball", indication: "Wrist pronation pain, forearm strain", notes: "Evoluent vertical mouse reduces forearm rotation by 60°" },
      { name: "Voice recognition software (Dragon, Windows/Mac dictation)", indication: "Significant grip/dexterity limitation, SLE cognitive fatigue", notes: "Free built-in options available; Dragon Medical for clinical documentation" },
      { name: "Tablet with stylus", indication: "Reduced grip — stylus requires less force than pen", notes: "iPad with Apple Pencil, Surface Pro — assess hand function and digital literacy" },
    ],
  },
  {
    category: "Mobility Aids",
    icon: "🦯",
    description: "Walking and mobility aids for lower limb involvement in rheumatic conditions.",
    products: [
      { name: "Walking stick (standard / quad)", indication: "Unilateral lower limb pain or instability", notes: "Correct height: elbow at 15–20° flexion. Use on contralateral side to affected joint" },
      { name: "Forearm crutch", indication: "Upper limb weight-bearing capacity — assess grip for standard handles", notes: "Fischer crutch distributes load to forearm — suitable for RA where grip is limited" },
      { name: "Wheeled walker (rollator)", indication: "Bilateral lower limb involvement, fatigue, balance", notes: "4-wheeled preferred for RA — assess braking mechanism and grip" },
      { name: "Gutter frame (forearm-bearing frame)", indication: "RA wrist/MCP involvement preventing grip of standard frame", notes: "Distributes load to forearms; assess upper limb ROM and strength" },
      { name: "Scooter / powerchair", indication: "Severe fatigue, bilateral lower limb disease limiting walking distance", notes: "Consider DRUK referral, Access to Work for employment, Motability scheme assessment" },
    ],
  },
  {
    category: "Ergonomic Equipment",
    icon: "🖥️",
    description: "Workplace and home ergonomic equipment to reduce musculoskeletal load.",
    products: [
      { name: "Sit-stand desk", indication: "axSpA (avoid prolonged static postures), prolonged sitting intolerance", notes: "NHS can fund via Access to Work scheme for employed patients; review task demands" },
      { name: "Monitor arm / laptop stand", indication: "Cervical pain (axSpA, RA), posture", notes: "Monitor at eye level; arm allows frequent repositioning" },
      { name: "Document holder", indication: "Neck strain from desk-based reading", notes: "Positioned beside monitor to avoid sustained neck rotation" },
      { name: "Anti-fatigue mat", indication: "Prolonged standing intolerance, plantar fasciitis (PsA)", notes: "Reduces ground reaction force during standing tasks" },
      { name: "Wrist rest (gel or foam)", indication: "Wrist pain during typing", notes: "Used during pauses — not during active typing. Assess wrist position" },
    ],
  },
  {
    category: "Communication Aids",
    icon: "📱",
    description: "Technology to support communication where hand function, vision, or fatigue limits traditional use.",
    products: [
      { name: "Smartphone accessibility settings (large text, voice control)", indication: "Reduced dexterity, visual impairment", notes: "iOS: Accessibility > Voice Control / Assistive Touch. Android equivalent" },
      { name: "Smart speaker (Alexa, Google Home)", indication: "Fatigue, reduced hand function — hands-free home control", notes: "Can control lights, reminders, timers — reduces physical effort" },
      { name: "Page turner / book holder", indication: "Grip, sustained static holding", notes: "Electric page turners available for severe grip limitation" },
      { name: "Key turner / key holder adaptor", indication: "Reduced grip, wrist rotation, Z-thumb", notes: "Lever-type key adaptor dramatically reduces force required" },
    ],
  },
];

const PRESCRIPTION_PRINCIPLES = [
  { principle: "Activity analysis first", detail: "Identify the specific functional barrier before prescribing. What is the task? What movement/strength/endurance is required? At which point does it break down?" },
  { principle: "Trial before prescription", detail: "Where possible, trial equipment before formal prescription. Patient preferences and practical factors (storage, lifestyle) affect adherence." },
  { principle: "Teach, don't just prescribe", detail: "Equipment must be taught. Assess technique. Poor technique with good equipment is often less functional than good technique without it." },
  { principle: "Review and follow up", detail: "AT prescription is not a one-off. Review at 4–8 weeks. Disease progression, deconditioning, or environmental change may require modification." },
  { principle: "Least restrictive option", detail: "Prescribe the simplest, least intrusive device that meets the functional need. Complex equipment with poor adherence delivers no benefit." },
  { principle: "Consider carer impact", detail: "AT that increases patient independence may also reduce carer burden. Consider carer as part of the assessment where relevant." },
];

export default function AssistiveTechnology() {
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  return (
    <OTLayout title="Assistive Technology">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>Living Made Easy (DLF):</strong> The Disabled Living Foundation's Living Made Easy website (<a href="https://livingmadeeasy.org.uk" target="_blank" rel="noopener noreferrer" className="underline">livingmadeeasy.org.uk</a>) is the UK's definitive AT resource — searchable by task and disability type. Recommend to patients directly.
        </div>

        {/* Prescription Principles */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">AT Prescription Principles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRESCRIPTION_PRINCIPLES.map((p) => (
              <div key={p.principle} className="bg-white rounded-2xl border border-slate-200 p-5">
                <h3 className="font-semibold text-amber-700 mb-2 text-sm">{p.principle}</h3>
                <p className="text-sm text-slate-600">{p.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* AT Categories */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">AT Category Browser</h2>
          <div className="space-y-4">
            {AT_CATEGORIES.map((cat) => (
              <div key={cat.category} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                  onClick={() => setOpenCategory(openCategory === cat.category ? null : cat.category)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <div>
                      <h3 className="font-bold text-slate-900">{cat.category}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{cat.description}</p>
                    </div>
                  </div>
                  <span className="text-slate-400 shrink-0 ml-4">{openCategory === cat.category ? "▲" : "▼"}</span>
                </button>

                {openCategory === cat.category && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {cat.products.map((p) => (
                      <div key={p.name} className="px-6 py-4">
                        <div className="flex items-start justify-between gap-4 mb-1">
                          <h4 className="font-semibold text-slate-900 text-sm">{p.name}</h4>
                        </div>
                        <p className="text-xs text-amber-700 mb-1"><span className="font-medium">Indication:</span> {p.indication}</p>
                        <p className="text-xs text-slate-500"><span className="font-medium">Notes:</span> {p.notes}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Home Assessment */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-bold text-slate-900">Home Assessment — Key Considerations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { area: "Entrance", points: ["Step/threshold height", "Key turning — consider key turner or smart lock", "Door handle type — lever preferred over round knob"] },
              { area: "Bathroom", points: ["Shower over bath vs wet room", "Grab rail positioning (BSI 8300 guidance)", "Toilet height — raised toilet seat if needed", "Tap type — lever vs cross-head"] },
              { area: "Kitchen", points: ["Worktop height relative to elbow", "Appliance accessibility", "Storage height — avoid overhead and floor level for most-used items"] },
              { area: "Bedroom", points: ["Bed height", "Lighting accessibility", "Clothing storage at accessible height", "Phone/communication at bedside"] },
              { area: "Stairs & Access", points: ["Handrail on both sides if possible", "Stairlift assessment if needed (refer to local authority OT)", "Level access alternatives — downstairs bedroom assessment if stairs unsafe"] },
              { area: "Outdoor", points: ["Pathway surfaces (uneven flagstones, gravel — hazardous)", "Step to front/back door", "Garden accessibility — raised beds, kneeling frames"] },
            ].map((a) => (
              <div key={a.area} className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-900 mb-2 text-sm">{a.area}</h4>
                <ul className="space-y-1">
                  {a.points.map((p) => (
                    <li key={p} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <span className="text-amber-500 mt-0.5">•</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <strong>Referral pathways:</strong> Major home modifications (ramps, widened doorways, wet rooms) require Local Authority OT referral for Disabled Facilities Grant (DFG) assessment. DFG covers adaptations up to £30,000 in England (2024). Means-tested. Refer early — waiting lists can be significant.
          </div>
        </section>

        {/* NICE AT Guidance */}
        <section className="bg-slate-900 text-white rounded-2xl p-6 space-y-3">
          <h3 className="font-bold">NICE Guidance — Assistive Technology in Rheumatology</h3>
          <ul className="text-sm text-slate-300 space-y-2">
            <li>• <strong className="text-white">NG100 (RA, 2018):</strong> Recommends OT assessment for hand function and prescription of assistive devices as part of comprehensive RA management.</li>
            <li>• <strong className="text-white">NICE Assistive Technology Guidance (MTG51, 2021):</strong> Covers digital/electronic AT; reminds clinicians to consider AT in rehabilitation pathways.</li>
            <li>• <strong className="text-white">NG206 (SLE, 2023):</strong> Includes OT referral recommendation for fatigue and functional independence in SLE.</li>
            <li>• <strong className="text-white">Living Made Easy (DLF):</strong> Endorsed by NICE as the reference resource for AT in self-management (livingmadeeasy.org.uk).</li>
          </ul>
        </section>

      </div>
    </OTLayout>
  );
}
